import type { EmployeeListItem } from '@/types/employee';
import { useCallback, useMemo, useState } from 'react';
import type { SelectedEmployee } from './types';
import { toSelectedEmployee } from './types';

/**
 * Hook for managing checkbox selection state in employee list.
 * Tracks which employees are checked and provides methods to manipulate selection.
 * Stores full employee data to persist selection across filter changes.
 */
export function useCheckboxSelection(availableEmployees: EmployeeListItem[]) {
  // Store full employee objects, not just IDs, to persist across filter changes
  const [checkedEmployees, setCheckedEmployees] = useState<Map<string, EmployeeListItem>>(
    new Map()
  );

  // Toggle single employee
  const toggle = useCallback(
    (id: string) => {
      setCheckedEmployees((prev) => {
        const next = new Map(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          // Find employee in current available list
          const employee = availableEmployees.find((emp) => emp.id === id);
          if (employee) {
            next.set(id, employee);
          }
        }
        return next;
      });
    },
    [availableEmployees]
  );

  // Check multiple employees
  const checkMany = useCallback(
    (ids: string[]) => {
      setCheckedEmployees((prev) => {
        const next = new Map(prev);
        ids.forEach((id) => {
          // Only add if not already checked and employee exists in available list
          if (!next.has(id)) {
            const employee = availableEmployees.find((emp) => emp.id === id);
            if (employee) {
              next.set(id, employee);
            }
          }
        });
        return next;
      });
    },
    [availableEmployees]
  );

  // Uncheck all
  const uncheckAll = useCallback(() => {
    setCheckedEmployees(new Map());
  }, []);

  // Check all visible employees
  const checkAllVisible = useCallback(
    (visibleIds: string[]) => {
      setCheckedEmployees((prev) => {
        const next = new Map(prev);
        visibleIds.forEach((id) => {
          const employee = availableEmployees.find((emp) => emp.id === id);
          if (employee) {
            next.set(id, employee);
          }
        });
        return next;
      });
    },
    [availableEmployees]
  );

  // Toggle all visible employees
  const toggleAllVisible = useCallback(
    (visibleIds: string[]) => {
      setCheckedEmployees((prev) => {
        const allChecked = visibleIds.every((id) => prev.has(id));
        const next = new Map(prev);

        if (allChecked) {
          // Uncheck all visible
          visibleIds.forEach((id) => next.delete(id));
        } else {
          // Check all visible
          visibleIds.forEach((id) => {
            if (!next.has(id)) {
              const employee = availableEmployees.find((emp) => emp.id === id);
              if (employee) {
                next.set(id, employee);
              }
            }
          });
        }
        return next;
      });
    },
    [availableEmployees]
  );

  // Get checked employees as EmployeeListItem[]
  // Returns all checked employees regardless of current filter
  const getCheckedEmployeesList = useCallback((): EmployeeListItem[] => {
    return Array.from(checkedEmployees.values());
  }, [checkedEmployees]);

  // Get checked employees as SelectedEmployee[]
  // Returns all checked employees regardless of current filter
  const getCheckedEmployees = useCallback((): SelectedEmployee[] => {
    return Array.from(checkedEmployees.values()).map(toSelectedEmployee);
  }, [checkedEmployees]);

  // Check if employee is checked
  const isChecked = useCallback((id: string) => checkedEmployees.has(id), [checkedEmployees]);

  // Count of checked employees
  const checkedCount = checkedEmployees.size;

  // Check if all visible are checked
  const areAllVisibleChecked = useCallback(
    (visibleIds: string[]) => {
      if (visibleIds.length === 0) return false;
      return visibleIds.every((id) => checkedEmployees.has(id));
    },
    [checkedEmployees]
  );

  // Check if some (but not all) visible are checked
  const areSomeVisibleChecked = useCallback(
    (visibleIds: string[]) => {
      if (visibleIds.length === 0) return false;
      const checkedVisible = visibleIds.filter((id) => checkedEmployees.has(id));
      return checkedVisible.length > 0 && checkedVisible.length < visibleIds.length;
    },
    [checkedEmployees]
  );

  // Get checked IDs set for backward compatibility if needed
  const checkedIds = useMemo(() => {
    return new Set(checkedEmployees.keys());
  }, [checkedEmployees]);

  return useMemo(
    () => ({
      checkedIds,
      toggle,
      checkMany,
      uncheckAll,
      checkAllVisible,
      toggleAllVisible,
      getCheckedEmployees,
      getCheckedEmployeesList,
      isChecked,
      checkedCount,
      areAllVisibleChecked,
      areSomeVisibleChecked,
    }),
    [
      checkedIds,
      toggle,
      checkMany,
      uncheckAll,
      checkAllVisible,
      toggleAllVisible,
      getCheckedEmployees,
      getCheckedEmployeesList,
      isChecked,
      checkedCount,
      areAllVisibleChecked,
      areSomeVisibleChecked,
    ]
  );
}

export type CheckboxSelectionState = ReturnType<typeof useCheckboxSelection>;
